// SETTINGS V2 — guest-pass row kebab menu (GP-MENU §7.2). Structural mirror of
// TeamRowV2Menu: Radix DropdownMenu with the SP3/SP4 portal scope-class trick,
// `⋮` trigger that stops propagation (a phone card-tap must not open the menu and
// a kebab-tap must not toggle the card), 44px touch target on phone / 28px on
// desktop, `sv2-menu-item` items.
//
// Item set is decided by the pass SOURCE, not by the operator:
//  • "Nový PIN"      — both sources. On DEMO rows it additionally sits inside
//    PermissionGuard(Users, Admin), mirroring the server gate on
//    POST /guest-passes/demo/{id}/regenerate-pin (Admin + Users:Admin) exactly as
//    the delete item in TeamRowV2Menu mirrors its own. Defence in depth: per
//    GP-13 only an Admin reaches /settings/team today, but this guard must ship
//    so a future Settings-access change cannot silently widen the demo surface.
//  • "Poslať znova"  — GUEST rows only (see the comment at the item).
//  • "Zrušiť"        — both sources, last, red.
//
// The caller renders this only for canWrite(Machinery) + Active rows, so no item
// here re-checks those. Inline styles + scoped CSS-var tokens + Radix. No
// MUI/sx/dt/Tailwind. SEC-REQ-2: no side effects.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";
import { GuestPass, GuestPassSource } from "../../models/guestPassModels";

export interface GuestPassRowV2MenuProps {
  pass: GuestPass;
  onResend: (pass: GuestPass) => void;
  onRegeneratePin: (pass: GuestPass) => void;
  onRevoke: (pass: GuestPass) => void;
}

const GuestPassRowV2Menu: React.FC<GuestPassRowV2MenuProps> = ({
  pass,
  onResend,
  onRegeneratePin,
  onRevoke,
}) => {
  const { translate: t } = useTranslation("settings");
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);
  const { isPhone } = useBreakpoint();

  const isDemo = pass.source === GuestPassSource.Demo;
  const size = isPhone ? 44 : 28;

  const regenerateItem = (
    <DropdownMenu.Item className="sv2-menu-item" onSelect={() => onRegeneratePin(pass)}>
      {t("guestPass.regeneratePin") as string}
    </DropdownMenu.Item>
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="btn btn-ghost"
          aria-label={t("guestPass.actionsAria") as string}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            width: size,
            minWidth: size,
            height: size,
            padding: 0,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ⋮
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`settings-v2-root p-${theme} mod-nastavenia sv2-menu`}
          align="end"
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
        >
          {isDemo ? (
            <PermissionGuard module={Modules.Users} level={PermissionLevel.Admin}>
              {regenerateItem}
            </PermissionGuard>
          ) : (
            regenerateItem
          )}

          {/* Resend stays hidden on DEMO rows. Two reasons, both still true:
              demo passes send NO email on any code path (owner ruling
              2026-08-04), so "resend" has no delivery channel; and the reissue
              response carries no PIN to show in its place. The screen-channel
              equivalent for demo rows is "Nový PIN" above. */}
          {!isDemo && (
            <DropdownMenu.Item className="sv2-menu-item" onSelect={() => onResend(pass)}>
              {t("guestPass.resend") as string}
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Item
            className="sv2-menu-item"
            onSelect={() => onRevoke(pass)}
            style={{ color: "var(--red)" }}
          >
            {t("guestPass.revoke") as string}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default GuestPassRowV2Menu;
